import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PlatformInfo {
  platform: 'ios' | 'android' | 'desktop' | 'unknown';
  isStandalone: boolean;
  canInstall: boolean;
}

export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [platformInfo, setPlatformInfo] = useState<PlatformInfo>({
    platform: 'unknown',
    isStandalone: false,
    canInstall: false,
  });

  // Detect platform
  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                        (window.navigator as any).standalone === true;
    
    let platform: PlatformInfo['platform'] = 'unknown';
    if (/iphone|ipad|ipod/.test(userAgent)) {
      platform = 'ios';
    } else if (/android/.test(userAgent)) {
      platform = 'android';
    } else {
      platform = 'desktop';
    }

    setPlatformInfo({
      platform,
      isStandalone,
      canInstall: !isStandalone,
    });
  }, []);

  // Track analytics event
  const trackEvent = useCallback(async (eventType: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      await supabase.from('install_analytics').insert({
        user_id: user?.id || null,
        event_type: eventType,
        platform: platformInfo.platform,
        user_agent: navigator.userAgent,
        referrer: document.referrer || null,
      });
    } catch (error) {
      console.error('Failed to track install event:', error);
    }
  }, [platformInfo.platform]);

  // Listen for beforeinstallprompt event
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      setDeferredPrompt(promptEvent);
      trackEvent('prompt_shown');
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, [trackEvent]);

  // Handle install action
  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        await trackEvent('prompt_accepted');
      } else {
        await trackEvent('prompt_dismissed');
      }
      
      setDeferredPrompt(null);
      return outcome;
    } catch (error) {
      console.error('Install prompt failed:', error);
      return 'dismissed';
    }
  }, [deferredPrompt, trackEvent]);

  // Track manual install (iOS, etc.)
  const trackManualInstall = useCallback(async () => {
    await trackEvent('manual_install');
  }, [trackEvent]);

  // Track welcome screen completion
  const trackWelcomeCompleted = useCallback(async () => {
    await trackEvent('welcome_completed');
  }, [trackEvent]);

  return {
    deferredPrompt,
    platformInfo,
    handleInstall,
    trackManualInstall,
    trackWelcomeCompleted,
  };
}
